import { Button, Empty, Flex, Input, Spin, Tooltip, Typography } from 'antd';
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { SendOutlined } from '@ant-design/icons';
import { useDebounce } from 'use-debounce';

import Styles from './styles';
import { useClip } from '../../contexts/clip/hook';
import { CommentLine } from '../CommentLine';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../redux/selectors';

const {
  CommentsContainer,
  AddCommentContainer,
  CommentsSearchContainer,
  CommentsListContainer,
  NoCommentFoundContainer,
} = Styles;
const { Search } = Input;

export const Comments: React.FC = () => {
  const { clip, isOwner, comment, deleteComment } = useClip();
  const user = useSelector(selectUser);
  const hasUser = !!user?.id;

  const [inputValue, setInputValue] = useState('');
  const [commentValue, setCommentValue] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      scrollToBottom();
    }
  }, []);

  const [searchDebounce] = useDebounce(inputValue, 300);
  const sanitizeSearchDebounce = (): [string, RegExp] => {
    const cleanSearch = searchDebounce.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    return [cleanSearch, new RegExp(`(${cleanSearch})`, 'i')];
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchString = e.currentTarget.value;
    setInputValue(searchString);
  };

  const commentsContent = useMemo(() => {
    const [search, regex] = sanitizeSearchDebounce();
    const filteredComments = !search
      ? clip.comments
      : clip.comments.filter(comment => comment.comment.search(regex) !== -1);

    if (!filteredComments.length && search !== '') {
      return <NoCommentFoundContainer>No matches for "{searchDebounce}"</NoCommentFoundContainer>;
    }

    return filteredComments.map((comment, index) => {
      return (
        <CommentLine
          key={index}
          id={comment.id}
          speaker={comment.user}
          content={comment.comment}
          createdAt={comment.createdAt}
          showDeleteButton={isOwner}
          onDelete={() => isOwner && deleteComment(comment.id)}
        />
      );
    });
  }, [clip.comments, searchDebounce]);

  const scrollToBottom = async () => {
    setTimeout(() => {
      containerRef.current!.scrollTo({ top: 9999, behavior: 'smooth' });
    }, 300);
  };

  const handleComment = async () => {
    setCommentValue('');
    setIsSending(true);
    await scrollToBottom();
    await comment(commentValue);
    await scrollToBottom();
    setIsSending(false);
  };

  return (
    <CommentsContainer>
      <CommentsSearchContainer>
        <Search placeholder="Search comment" value={inputValue} onChange={onSearchChange} />
      </CommentsSearchContainer>

      <CommentsListContainer ref={containerRef}>
        {commentsContent}

        {isSending && (
          <Flex justify="center" style={{ width: '100%' }}>
            <Spin />
          </Flex>
        )}

        {clip.comments.length === 0 && (
          <NoCommentFoundContainer>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments found" />
          </NoCommentFoundContainer>
        )}
      </CommentsListContainer>

      <AddCommentContainer>
        <Input
          value={commentValue}
          placeholder="Add a comment to this clip"
          onChange={e => setCommentValue(e.target.value)}
          onKeyUp={e => e.key === 'Enter' && commentValue.length > 0 && handleComment()}
          disabled={!hasUser}
        />
        <Tooltip title={!hasUser ? 'You need to be logged in to add a comment' : null}>
          <Button
            onClick={handleComment}
            disabled={commentValue.length === 0 || !hasUser}
            type="primary"
            icon={<SendOutlined />}
          >
            Comment
          </Button>
        </Tooltip>
      </AddCommentContainer>
    </CommentsContainer>
  );
};
